import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Building2,
  Activity,
  Calendar,
  Download,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import NeonUsagePanel from '../../components/NeonUsagePanel';

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAnalytics({ dateRange });
      setData(response.data.data);
    } catch (err) {
      toast.error('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleExport = async () => {
    try {
      toast.success('Export started. You will receive an email when ready.');
      await adminApi.exportAnalytics({ dateRange });
    } catch (err) {
      toast.error('Failed to export analytics');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">Track key metrics and performance</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={handleExport} className="btn-secondary">
            <Download className="w-5 h-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="w-4 h-4 mr-1" />
              +{data?.revenue?.growth || 0}%
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">${data?.revenue?.mrr || '0.00'}</h3>
          <p className="text-sm text-gray-500 mt-1">Monthly Recurring Revenue</p>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">ARR</span>
              <span className="font-medium text-gray-900">${data?.revenue?.arr || '0.00'}</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="w-4 h-4 mr-1" />
              +{data?.tenants?.newThisMonth || 0}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{data?.tenants?.total || 0}</h3>
          <p className="text-sm text-gray-500 mt-1">Total Tenants</p>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Active</span>
              <span className="font-medium text-gray-900">{data?.tenants?.active || 0}</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="w-4 h-4 mr-1" />
              +{data?.users?.newThisMonth || 0}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{data?.users?.total || 0}</h3>
          <p className="text-sm text-gray-500 mt-1">Total Users</p>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Active Today</span>
              <span className="font-medium text-gray-900">{data?.users?.activeToday || 0}</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Activity className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{data?.churn?.rate || 0}%</h3>
          <p className="text-sm text-gray-500 mt-1">Churn Rate</p>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Churned This Month</span>
              <span className="font-medium text-gray-900">{data?.churn?.count || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Revenue chart placeholder</p>
              <p className="text-xs text-gray-400 mt-1">Integrate with your charting library</p>
            </div>
          </div>
        </div>

        {/* Tenant Growth Chart */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Tenant Growth</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Growth chart placeholder</p>
              <p className="text-xs text-gray-400 mt-1">Integrate with your charting library</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plan Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Distribution</h3>
          <div className="space-y-4">
            {(data?.planDistribution || [
              { name: 'Starter', count: 45, percentage: 30 },
              { name: 'Professional', count: 75, percentage: 50 },
              { name: 'Enterprise', count: 30, percentage: 20 }
            ]).map((plan, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{plan.name}</span>
                  <span className="font-medium text-gray-900">{plan.count} ({plan.percentage}%)</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-full bg-primary-500 rounded-full"
                    style={{ width: `${plan.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Website Visitors</span>
              <span className="font-semibold text-gray-900">{data?.funnel?.visitors || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Trial Signups</span>
              <span className="font-semibold text-gray-900">{data?.funnel?.trials || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Paid Conversions</span>
              <span className="font-semibold text-gray-900">{data?.funnel?.paid || 0}</span>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Trial to Paid Rate</span>
                <span className="font-semibold text-green-600">
                  {data?.funnel?.conversionRate || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Tenants by Usage */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tenants by Usage</h3>
          <div className="space-y-3">
            {(data?.topTenants || [
              { name: 'ABC Accounting', users: 25, storage: '45 GB' },
              { name: 'XYZ Partners', users: 20, storage: '38 GB' },
              { name: 'Smith & Co', users: 18, storage: '32 GB' },
              { name: 'Elite Tax', users: 15, storage: '28 GB' },
              { name: 'Premier CPA', users: 12, storage: '22 GB' }
            ]).map((tenant, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 mr-3">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{tenant.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">{tenant.users} users</p>
                  <p className="text-xs text-gray-500">{tenant.storage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Database Costs - Neon Usage */}
      <div className="card p-6">
        <NeonUsagePanel showTenantBreakdown={true} />
      </div>

      {/* Activity Metrics */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.logins || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Logins Today</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.apiCalls || 0}</p>
            <p className="text-xs text-gray-500 mt-1">API Calls</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.documentsCreated || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Documents Created</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.emailsSent || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Emails Sent</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.supportTickets || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Support Tickets</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{data?.activity?.avgResponseTime || 0}s</p>
            <p className="text-xs text-gray-500 mt-1">Avg Response Time</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
